/**
 * WalletButton - Wallet connection button with address display.
 *
 * Features:
 * - Connect wallet prompt
 * - Connected state with truncated address
 * - Disconnect dropdown
 * - Loading state during connection
 */

import { useState, useRef, useEffect } from 'react';
import { useWeb3 } from '../contexts/Web3Context';

const WalletButton = () => {
  const { account, isConnected, isConnecting, connect, disconnect } = useWeb3();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Truncate address: 0x1234...5678
  const truncateAddress = (address: string): string => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Copy address to clipboard
  const copyAddress = async () => {
    if (account) {
      await navigator.clipboard.writeText(account);
      // Could add a toast notification here
    }
  };

  // Handle connect
  const handleConnect = async () => {
    await connect();
  };

  // Handle disconnect
  const handleDisconnect = async () => {
    await disconnect();
    setShowDropdown(false);
  };

  // Not connected state
  if (!isConnected) {
    return (
      <button
        onClick={handleConnect}
        disabled={isConnecting}
        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-accent text-slate-950 font-medium font-display shadow-glow hover:shadow-glow-lg hover:bg-accent-hover transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isConnecting ? (
          <>
            <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
            <span>Connecting...</span>
          </>
        ) : (
          <>
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <span className="hidden sm:inline">Connect Wallet</span>
            <span className="sm:hidden">Connect</span>
          </>
        )}
      </button>
    );
  }

  // Connected state
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 hover:bg-accent/15 transition-colors"
      >
        {/* Connected indicator */}
        <div className="w-2 h-2 bg-accent rounded-full animate-pulse-glow shrink-0" />

        {/* Address + Network */}
        <div className="flex flex-col items-start leading-tight">
          <span className="text-white font-medium text-sm">
            {account && truncateAddress(account)}
          </span>
          <span className="text-text-muted text-[10px]">Arbitrum</span>
        </div>

        {/* Dropdown arrow */}
        <svg
          className={`w-4 h-4 text-text-muted transition-transform ${
            showDropdown ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown menu */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-48 rounded-2xl bg-[rgba(15,23,42,0.9)] backdrop-blur-[20px] border border-white/5 shadow-card-lg animate-fade-in overflow-hidden">
          {/* Copy Address */}
          <button
            onClick={copyAddress}
            className="w-full px-4 py-3 text-left text-sm text-white hover:bg-white/5 transition-colors flex items-center gap-3"
          >
            <svg
              className="w-4 h-4 text-text-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            Copy Address
          </button>

          {/* View on Explorer */}
          <a
            href={`https://arbiscan.io/address/${account}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full px-4 py-3 text-left text-sm text-white hover:bg-white/5 transition-colors flex items-center gap-3"
          >
            <svg
              className="w-4 h-4 text-text-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
            View on Explorer
          </a>

          {/* Divider */}
          <div className="border-t border-white/5" />

          {/* Disconnect */}
          <button
            onClick={handleDisconnect}
            className="w-full px-4 py-3 text-left text-sm text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-3"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Disconnect
          </button>
        </div>
      )}
    </div>
  );
};

export default WalletButton;
